import { useEffect, useState } from "react";
import { FiLoader, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { fetchAppInfo } from "../helpers/global_service";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Custom styles for ReactQuill read-only display
const quillReadOnlyStyles = `
  .react-quill-readonly .ql-editor {
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    font-family: inherit !important;
    line-height: 1.6 !important;
    color: inherit !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-container {
    border: none !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-toolbar {
    display: none !important;
  }
  
  .react-quill-readonly .ql-editor.ql-blank::before {
    display: none !important;
  }
`;

interface AppInfoResponse {
  statusCode: number;
  message: string;
  response: string;
}

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const [appInfo, setAppInfo] = useState<AppInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inject ReactQuill read-only styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = quillReadOnlyStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 2 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRetry = () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 2 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full px-4 py-8">
        {/* Content Container */}
        <div className="w-full max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t("pages.privacyPolicy.title")}
            </h1>
            <div className="w-20 h-1 bg-primaryColor rounded"></div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:p-8">
            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiLoader className="h-12 w-12 text-primaryColor animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.privacyPolicy.loading")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  {t("pages.privacyPolicy.loadingDescription")}
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiAlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.privacyPolicy.errorTitle")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                  {error}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 transition-colors duration-200"
                >
                  <FiRefreshCw className="h-4 w-4 mr-2" />
                  {t("pages.privacyPolicy.retryButton")}
                </button>
              </div>
            )}

            {/* Success State - Display API Content */}
            {appInfo && !loading && !error && (
              <div className="react-quill-readonly">
                <ReactQuill
                  value={appInfo.response}
                  readOnly={true}
                  theme="snow"
                  modules={{
                    toolbar: false,
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                  }}
                />
              </div>
            )}

            {/* Fallback Content - Show if API fails and no retry attempted */}
            {!appInfo && !loading && !error && (
              <div className="prose prose-gray dark:prose-invert max-w-none">
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6">
                  Last updated: {new Date().toLocaleDateString()}
                </p>

                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  Privacy Policy
                </h2>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  At Bossie, we are committed to protecting your privacy and
                  ensuring the security of your personal information. This
                  Privacy Policy explains how we collect, use, disclose, and
                  safeguard your information when you use our application and
                  services.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Information We Collect
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We collect information you provide directly to us, such as
                  when you create an account, use our services, or contact us
                  for support. This may include:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                  <li>Name, email address, and contact information</li>
                  <li>Account credentials and profile information</li>
                  <li>Usage data and analytics</li>
                  <li>Communication preferences</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  How We Use Your Information
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                  <li>Provide and maintain our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send technical notices and support messages</li>
                  <li>Respond to your comments and questions</li>
                  <li>Improve our services and develop new features</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Information Sharing
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We do not sell, trade, or otherwise transfer your personal
                  information to third parties without your consent, except as
                  described in this policy or as required by law.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Data Security
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We implement appropriate security measures to protect your
                  personal information against unauthorized access, alteration,
                  disclosure, or destruction.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Your Rights
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  You have the right to access, update, or delete your personal
                  information. You may also opt out of certain communications
                  from us.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Contact Us
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  If you have any questions about this Privacy Policy, please
                  contact us at{" "}
                  <a
                    href="mailto:support@bossie.com"
                    className="text-primaryColor hover:underline"
                  >
                    support@bossie.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
